import { useEffect, useRef } from "react";
import Map from "@arcgis/core/Map";
import MapView from "@arcgis/core/views/MapView";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import { setAssetPath as setCalciteComponentsAssetPath } from '@esri/calcite-components';
import { DEFAULT_CENTER, DEFAULT_FILL_SYMBOL } from "./constants";
import Search from "@arcgis/core/widgets/Search";
import Sketch from "@arcgis/core/widgets/Sketch";
setCalciteComponentsAssetPath("https://js.arcgis.com/calcite-components/2.13.2/assets");
import { TNullable } from "../../../common/types/common";
import Popup from "@arcgis/core/widgets/Popup";

import "./styles.scss";

type TOnInitFnWithSketch = { sketch: Sketch };
type TOnInitFnWithSearchWidget = { searchWidget: Search };
type TOnInitFnDefaultParams = {
    graphicsLayer: GraphicsLayer,
    view: MapView,
};

type TOnInitFn<TParams> = (_params: TParams) => void;

type Props =
| {
    withSearch: true
    withSketch: true,
    onInit?: TNullable<TOnInitFn<TOnInitFnDefaultParams & TOnInitFnWithSearchWidget & TOnInitFnWithSketch>>,
}
| {
    withSearch?: false
    withSketch: true,
    onInit?: TNullable<TOnInitFn<TOnInitFnDefaultParams & TOnInitFnWithSketch>>,
}
| {
    withSearch: true
    withSketch?: false,
    onInit?: TNullable<TOnInitFn<TOnInitFnDefaultParams & TOnInitFnWithSearchWidget>>,
}
| {
    withSearch?: false
    withSketch?: false,
    onInit?: TNullable<TOnInitFn<TOnInitFnDefaultParams>>,
}

const ArcGisMap = ({ withSearch = false, withSketch = false, onInit = null }: Props) => {
    const mapRef = useRef(null);

    useEffect(() => {
        if (mapRef.current) {
            const graphicsLayer = new GraphicsLayer();
            let searchWidget: TNullable<Search> = null;
            let sketch: TNullable<Sketch> = null;

            const map = new Map({
                basemap: "satellite",
                layers: [graphicsLayer],
            });

            const view = new MapView({
                container: mapRef.current!,
                map: map,
                center: DEFAULT_CENTER,
                zoom: 13,
                ui: {
                    components: [], // Disable all default UI components
                },
                popup: new Popup({
                    alignment: "bottom-center",
                }),
            });

            if (withSearch) {
                // Add Search widget to the map
                searchWidget = new Search({
                    view, // Attach the search widget to the view
                    disabled: true,
                });

                // Add the search widget to the top-right corner of the view
                view.ui.add(searchWidget, {
                    position: "top-left",
                });
            }

            if (withSketch) {
                sketch = new Sketch({
                    view: view,
                    layer: graphicsLayer,
                    availableCreateTools: ["polygon"],
                    visibleElements: {
                        createTools: {
                            point: false,
                            polyline: false,
                            circle: false,
                        },
                        selectionTools: {
                            "lasso-selection": false,
                            "rectangle-selection": false,
                        },
                        undoRedoMenu: false,
                        settingsMenu: false,
                    }

                } as any);

                sketch.viewModel.polygonSymbol = DEFAULT_FILL_SYMBOL;

                view.ui.add(sketch, "top-right");
            }

            if (onInit) {
                const params: any = {
                    graphicsLayer,
                    view,
                };

                if (withSearch) {
                    params['searchWidget'] = searchWidget;
                }

                if (withSketch) {
                    params['sketch'] = sketch;
                }

                onInit(params);
            }
        }
    }, []);

    return <div style={{ height: "100%", width: "100%" }} ref={mapRef}></div>;
};

export default ArcGisMap;
